import { useState, useEffect } from 'react'
import type { Payer } from '@alpha-edge/shared'
import {
  Modal,
  FormField,
  btnPrimary,
  btnSecondary,
  btnDanger,
  colors,
  input,
  ConfirmDialog,
} from '../../components/ui'
import { useModal } from '../../hooks/use-modal'
import { api, ApiError } from '../../services/api'

interface PayerForm {
  code: string
  name: string
  payerType: string
  ediId: string
  claimFormat: string
}

const defaults: PayerForm = {
  code: '',
  name: '',
  payerType: 'commercial',
  ediId: '',
  claimFormat: '',
}

interface Props {
  readonly payerId: string | null
  readonly isOpen: boolean
  readonly isNew: boolean
  readonly onClose: () => void
  readonly onSaved: (p: Payer) => void
  readonly onArchived?: (id: string) => void
}

export function PayerEditModal({ payerId, isOpen, isNew, onClose, onSaved, onArchived }: Props) {
  const [form, setForm] = useState<PayerForm>(defaults)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSystem, setIsSystem] = useState(false)
  const archiveModal = useModal()
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    if (isNew) {
      setForm(defaults)
      setError(null)
      setIsSystem(false)
      return
    }
    if (!payerId) return
    let cancelled = false
    setLoading(true)
    setError(null)
    api
      .get<Payer>(`/payers/${payerId}`)
      .then((res) => {
        if (cancelled) return
        if (res.data) {
          const p = res.data
          setForm({
            code: p.code,
            name: p.name,
            payerType: p.payerType,
            ediId: p.ediId ?? '',
            claimFormat: p.claimFormat ?? '',
          })
          setIsSystem(p.isSystem)
        }
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof ApiError ? err.message : 'Failed to load')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [payerId, isOpen, isNew])

  const update = <K extends keyof PayerForm>(k: K, v: PayerForm[K]) => setForm((prev) => ({ ...prev, [k]: v }))

  async function handleSave() {
    setSaving(true)
    setError(null)
    try {
      const payload: Record<string, unknown> = {
        code: form.code,
        name: form.name,
        payerType: form.payerType,
      }
      if (form.ediId) payload.ediId = form.ediId
      if (form.claimFormat) payload.claimFormat = form.claimFormat
      const res = isNew
        ? await api.post<Payer>('/payers', payload)
        : await api.put<Payer>(`/payers/${payerId}`, payload)
      if (!res.data) throw new Error('No data returned')
      onSaved(res.data)
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  async function handleArchive() {
    if (!payerId) return
    setArchiving(true)
    try {
      await api.delete(`/payers/${payerId}`)
      onArchived?.(payerId)
      archiveModal.close()
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to archive')
    } finally {
      setArchiving(false)
    }
  }

  const title = isNew ? 'New Payer' : `${form.code} - ${form.name}`

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={title} width="640px">
        {loading ? (
          <div style={{ color: colors.textMuted, padding: '1rem 0' }}>Loading...</div>
        ) : (
          <div>
            {isSystem && (
              <div
                style={{
                  padding: '0.625rem 0.875rem',
                  marginBottom: '1rem',
                  background: '#fef3c7',
                  border: '1px solid #f59e0b',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  color: '#92400e',
                }}
                data-testid="system-record-notice"
              >
                This is a system-provided payer and cannot be edited or archived.
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '0.75rem' }}>
              <FormField
                name="code"
                label="Code"
                required
                value={form.code}
                onChange={(v) => update('code', v)}
                disabled={isSystem}
              />
              <FormField
                name="name"
                label="Name"
                required
                value={form.name}
                onChange={(v) => update('name', v)}
                disabled={isSystem}
              />
              <FormField name="payerType" label="Type" required>
                <select
                  style={input}
                  value={form.payerType}
                  onChange={(e) => update('payerType', e.target.value)}
                  disabled={isSystem}
                >
                  <option value="commercial">Commercial</option>
                  <option value="medicare">Medicare</option>
                  <option value="medicaid">Medicaid</option>
                  <option value="self_pay">Self-pay</option>
                  <option value="other">Other</option>
                </select>
              </FormField>
              <FormField
                name="ediId"
                label="EDI ID"
                value={form.ediId}
                onChange={(v) => update('ediId', v)}
                disabled={isSystem}
              />
              <FormField
                name="claimFormat"
                label="Claim Format"
                value={form.claimFormat}
                onChange={(v) => update('claimFormat', v)}
                disabled={isSystem}
              />
            </div>
            {error && (
              <div
                style={{
                  marginTop: '0.75rem',
                  color: colors.danger,
                  padding: '0.5rem 0.75rem',
                  background: colors.bgError,
                  border: `1px solid ${colors.borderError}`,
                  borderRadius: '3px',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              {!isNew && !isSystem && onArchived && (
                <button
                  type="button"
                  onClick={archiveModal.open}
                  style={{ ...btnDanger, marginRight: 'auto' }}
                  disabled={saving || archiving}
                >
                  Archive
                </button>
              )}
              <button type="button" onClick={onClose} style={btnSecondary} disabled={saving}>
                {isSystem ? 'Close' : 'Cancel'}
              </button>
              {!isSystem && (
                <button type="button" onClick={handleSave} style={btnPrimary} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog
        isOpen={archiveModal.isOpen}
        onClose={archiveModal.close}
        onConfirm={handleArchive}
        title="Archive Payer"
        message={`Archive ${form.name}? Existing patient assignments are preserved but new orders cannot select this payer.`}
        confirmLabel="Archive"
        loading={archiving}
      />
    </>
  )
}
